import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../auth/msalConfig';
import { fetchResponseRates, type SurveyResponseRateReport, type TeamResponseRateDto } from '../../api/surveyApi';
import { EngagementExportModal } from './ExportModal';

interface Props {
  surveyId: string;
}

function TeamCard({ team, threshold }: { team: TeamResponseRateDto; threshold: number }) {
  if (team.belowAnonymityThreshold) {
    return (
      <div className="team-card team-card--suppressed">
        <div className="team-card__header">
          <span className="team-card__name">{team.teamLabel}</span>
        </div>
        <p className="team-card__suppressed-msg">
          Results suppressed — fewer than {threshold} responses
        </p>
      </div>
    );
  }

  const pct = team.totalInvited > 0
    ? Math.round((team.respondedCount / team.totalInvited) * 100)
    : 0;

  return (
    <div className="team-card">
      <div className="team-card__header">
        <span className="team-card__name">{team.teamLabel}</span>
        <span className="team-card__rate">{pct}%</span>
      </div>
      <div className="team-card__bar-track">
        <div className="team-card__bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="team-card__count">
        {team.respondedCount} of {team.totalInvited} responded
      </span>
    </div>
  );
}

export function SurveyResponseRates({ surveyId }: Props) {
  const { instance, accounts } = useMsal();
  const [report, setReport] = useState<SurveyResponseRateReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showExport, setShowExport] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const tokenResponse = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });
        const data = await fetchResponseRates(surveyId, tokenResponse.accessToken);
        setReport(data);
      } catch {
        setError('Could not load response rates.');
      }
    };
    load();
  }, [surveyId, instance, accounts]);

  if (error) {
    return <p style={{ color: 'var(--color-error)' }}>{error}</p>;
  }
  if (!report) {
    return <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>;
  }

  const total = report.teams.reduce(
    (acc, t) => ({ responded: acc.responded + t.respondedCount, invited: acc.invited + t.totalInvited }),
    { responded: 0, invited: 0 }
  );
  const overallPct = total.invited > 0 ? Math.round((total.responded / total.invited) * 100) : 0;

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 1.25rem' }}>
      {/* Summary strip */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.25rem' }}>
            {report.surveyTitle}
          </h2>
          <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>
            Overall response rate:{' '}
            <strong style={{ color: 'var(--color-text)' }}>
              {total.responded}/{total.invited} ({overallPct}%)
            </strong>
          </p>
        </div>
        <button
          className="btn btn-ghost"
          style={{ minHeight: 36, padding: '0 1rem', fontSize: '0.875rem', whiteSpace: 'nowrap' }}
          onClick={() => setShowExport(true)}
        >
          Export results
        </button>
      </div>

      {showExport && (
        <EngagementExportModal
          surveyId={surveyId}
          surveyTitle={report.surveyTitle}
          onClose={() => setShowExport(false)}
        />
      )}

      {/* Mobile: card stack */}
      <div className="responsive-cards">
        {report.teams.map(team => (
          <TeamCard key={team.teamLabel} team={team} threshold={report.anonymityThreshold} />
        ))}
      </div>

      {/* Desktop: table */}
      <table className="responsive-table">
        <thead>
          <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
            {['Team', 'Responded', 'Invited', 'Rate'].map(h => (
              <th key={h} style={{ textAlign: 'left', padding: '0.6rem 0.75rem', fontWeight: 600 }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {report.teams.map(team => (
            <tr key={team.teamLabel} style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '0.6rem 0.75rem' }}>{team.teamLabel}</td>
              {team.belowAnonymityThreshold ? (
                <td colSpan={3} style={{ padding: '0.6rem 0.75rem', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                  Suppressed — fewer than {report.anonymityThreshold} responses
                </td>
              ) : (
                <>
                  <td style={{ padding: '0.6rem 0.75rem' }}>{team.respondedCount}</td>
                  <td style={{ padding: '0.6rem 0.75rem' }}>{team.totalInvited}</td>
                  <td style={{ padding: '0.6rem 0.75rem', fontWeight: 600 }}>
                    {Math.round((team.respondedCount / team.totalInvited) * 100)}%
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: '1rem' }}>
        Individual responses are never recorded. Results are suppressed for teams with fewer
        than {report.anonymityThreshold} responses.
      </p>
    </div>
  );
}
